import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';

import userForms from '../../requests/queries/userForms';
import userTasksQuery from '../../requests/queries/userTasksQuery';
import { getItem, setItem, removeItem } from '@helpers/sessionStorage';

const ANSWERS_CACHE_KEY_PREFIX = 'userFormAnswersCache:';

export function useUserTasks(username) {
  const cacheKey = `${ANSWERS_CACHE_KEY_PREFIX}${username || 'anon'}`;

  const { data: userFormsData = {} } = useQuery(userForms, {
    skip: !username
  });

  const [answersState, setAnswersState] = useState(null);

  const [loadAnswers, { data: networkData, loading: answersLoading }] =
    useLazyQuery(userTasksQuery, {
      fetchPolicy: 'network-only'
    });

  useEffect(() => {
    if (!username) return;

    if (!answersState) {
      let parsed = null;

      try {
        const cached = getItem(cacheKey);
        parsed = cached ? JSON.parse(cached) : null;
      } catch {
        parsed = null;
      }

      if (parsed?.me) {
        setAnswersState(parsed);
        return;
      }
    }

    if (!answersState) loadAnswers();
  }, [username, cacheKey]);

  useEffect(() => {
    if (!username || !networkData) return;

    setAnswersState(networkData);
    setItem(cacheKey, JSON.stringify(networkData));
  }, [networkData, username, cacheKey]);

  const refetchUserFormAnswers = useCallback(() => {
    if (!username) return;
    loadAnswers();
  }, [username, loadAnswers]);

  const clearUserFormAnswersCache = useCallback(() => {
    if (!username) return;
    removeItem(cacheKey);
    setAnswersState(null);
  }, [username, cacheKey]);

  const tasks = useMemo(() => {
    const me = answersState?.me;
    if (!me) return [];

    const formAnswers = me.formAnswers || [];

    const xformAnswers = (me.xformAnswers || []).filter(
      (xa) => xa.flowState === 'contact'
    );

    const pendingFormAnswers = formAnswers.filter(
      (fa) => fa.status === 'PENDING'
    );

    const providerForms =
      userFormsData?.me?.forms?.filter((f) => f.type === 'PROVIDER') || [];

    const sortableTasks = [...pendingFormAnswers, ...xformAnswers];

    sortableTasks.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return [...sortableTasks, ...providerForms];
  }, [answersState, userFormsData]);

  return {
    tasks,
    answersLoading,
    refetchUserFormAnswers,
    clearUserFormAnswersCache
  };
}
